// Types for REWagent Lead Management Platform

// Property types from schema
export type PropertyType = 'VCL' | 'SFH' | 'MFU' | 'CON' | 'APT' | 'TWH' | 'MOB' | 'COM' | 'IND' | 'AGR' | 'MIX' | 'LOT' | 'MNL';

export const PROPERTY_TYPE_LABELS: Record<PropertyType, string> = {
  VCL: 'Vacant Land',
  SFH: 'Single Family Home',
  MFU: 'Multi-Family Unit',
  CON: 'Condo',
  APT: 'Apartment',
  TWH: 'Townhouse',
  MOB: 'Mobile Home',
  COM: 'Commercial',
  IND: 'Industrial',
  AGR: 'Agricultural',
  MIX: 'Mixed Use',
  LOT: 'Lot',
  MNL: 'Manufactured Home'
};

// Lead status types
export type LeadStatus = 'new' | 'qualified' | 'disqualified';

export const STATUS_LABELS: Record<LeadStatus, { label: string; color: string }> = {
  new: { label: 'New', color: 'blue' },
  qualified: { label: 'Qualified', color: 'green' },
  disqualified: { label: 'Disqualified', color: 'red' }
};

// Lead interface matching database schema
export interface Lead {
  id: number;
  client_id: number;
  campaign_name: string;
  owner_firstname: string;
  owner_lastname: string;
  owner_phone: string;
  owner_email: string | null;
  prop_type: PropertyType;
  bedrooms: number | null;
  bathrooms: number | null;
  updates_text: string | null;
  land_size: number | null;
  land_utilities: number | null;
  prop_address: string;
  market_val: number;
  ask_price: number;
  prop_license: 0 | 1;
  occupancy: 0 | 1 | 2 | 3;
  listing: 0 | 1 | 2;
  mortgage: 0 | 1 | 2;
  mortgage_amount: number | null;
  owshp_tmln: number | null;
  sell_reason: number;
  closn_tmln: number;
  info: string | null;
  motivation: number;
  cond_scale: number;
  lead_score: number;
  lead_cost: number;
  lead_notes: string | null;
  lead_code: string;
  recording_url: string | null;
  status: LeadStatus;
  disqualify_reason: string | null;
  is_hidden_from_client: boolean;
  created_at: string;
  updated_at: string;
}

// Lead list item (for dashboard)
export interface LeadListItem {
  id: number;
  campaign_name: string;
  owner_firstname: string;
  owner_lastname: string;
  owner_phone: string;
  prop_address: string;
  prop_type: PropertyType;
  status: LeadStatus;
  lead_score: number;
  lead_cost: number;
  created_at: string;
}

// Status history entry
export interface StatusHistory {
  id: number;
  lead_id: number;
  team_member_id: number | null;
  old_status: string | null;
  new_status: string;
  reason: string | null;
  created_at: string;
}

// User session info
export interface User {
  id: number;
  name: string;
  client_id: number;
  client_name: string;
  balance?: number;
  role?: 'admin' | 'qa';
}

// Admin user
export interface AdminUser {
  id: string;
  username: string;
}

// Client
export interface Client {
  id: string;
  name: string;
  created_at: string;
  balance?: number;
  lead_count?: number;
}

// Team member
export interface TeamMember {
  id: string;
  name: string;
  client_name: string;
  is_active: number;
  access_code: string;
  role: 'admin' | 'qa';
  created_at: string;
}

// API key
export interface ApiKey {
  id: string;
  name: string;
  is_active: number;
  created_at: string;
}

// Pagination
export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// API Response types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface LeadsResponse {
  leads: LeadListItem[];
  pagination: Pagination;
}

export interface LeadDetailResponse {
  lead: Lead;
  status_history: StatusHistory[];
}

export interface LoginResponse {
  success: boolean;
  user?: User;
  error?: string;
}

export interface AdminLoginResponse {
  success: boolean;
  admin?: AdminUser;
  error?: string;
}
